import { useEffect } from "react";
import { RouterProvider } from "react-router-dom";
import { QueryClientProvider } from "@tanstack/react-query";
import { queryClient } from "@/api";
import { bindSessionEndToAuthStore } from "@/auth";
import { ToastProvider } from "@/ui";
import { applyTheme, useThemeStore } from "@/design/themeStore";
import { I18nProvider } from "./i18n";
import { applyDocumentLocale, useUiStore } from "./uiStore";
import { useOnlineStatus } from "./useOnlineStatus";
import { ErrorBoundary } from "./ErrorBoundary";
import { router } from "./router";

/** Reflect locale/dir onto <html> + run the offline detector. */
function DocumentEffects() {
  const locale = useUiStore((s) => s.locale);
  const dir = useUiStore((s) => s.dir);
  const theme = useThemeStore((s) => s.theme);
  useOnlineStatus();
  useEffect(() => {
    applyDocumentLocale(locale, dir);
  }, [locale, dir]);
  useEffect(() => {
    applyTheme(theme);
  }, [theme]);
  return null;
}

/**
 * App — the provider stack (Query → Toast → i18n) around the registry-composed router. The auth
 * store's session-end purge is bound to the token rotation flow once, at boot.
 */
export function App() {
  useEffect(() => {
    bindSessionEndToAuthStore();
  }, []);

  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <I18nProvider>
          <ToastProvider>
            <DocumentEffects />
            <RouterProvider router={router} />
          </ToastProvider>
        </I18nProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}
